import { Box, Fab, useTheme } from "@mui/material";
import UnsavedChangesPrompt from "../../components/unsaved-changes-prompt/UnsavedChangesPrompt";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { isMobile, isTablet } from "react-device-detect";
import { useTranslation } from "react-i18next";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { ManualEntryFormState } from "./ManualEntryFormViewState";
import { useRef  } from "react";
import { AppSettingsActionTypeEnum, useAppSettings } from "../../contexts/app-context/AppContext";
import { Save } from "@mui/icons-material";
import OptionsSideSheet from "../../components/options-side-sheet/OptionsSideSheet";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ContentContainer } from "../../components/content-container/ContentContainer";
import { ContentSection } from "../../components/content-section/ContentSection";
import { RestError } from "../../services/rest-service/RestService";
import { AppNotificationTypeEnum } from "../../contexts/app-notification-context/AppNotificationContextTypes";
import { useAppNotificationContext } from "../../contexts/app-notification-context/UseAppNotificationContext";
import TagValueDataService from "../../services/tag-value-data-service/TagValueDataService";
import { addSeconds } from "date-fns";
import ManualEntryForm from "../../components/manual-entry-form/ManualEntryForm";
import { ManualEntryFormMode, ManualEntryFormRefType, ManualEntryFormStateType } from "../../components/manual-entry-form/ManualEntryFormUtils";
import { IeLynxObject } from "../../common/models/eLynxObject";
import DefaultDatePicker from "../../components/default-date-picker/DefaultDatePicker";



export type ManualEntryFormContentProps = {
    elynxObject?: IeLynxObject;
    manualEntryFormState: ManualEntryFormState;
    hideOptions?: boolean;
}

export default function ManualEntryFormContent({ 
    elynxObject,
    manualEntryFormState,
    hideOptions
 
}: ManualEntryFormContentProps) {

    const isPhone = isMobile && !isTablet;
    const theme = useTheme();
    const { confirm, acknowledge } = useConfirmation();
    const {t} = useTranslation();
    const [offlineState] = useOfflineDetectContext();    
    const [userState] = useUserContext();
    const [appSettings, dispatchAppSettings] = useAppSettings();
    const [,setAppNotificationState] = useAppNotificationContext(); 

    const buttonRef = useRef<HTMLButtonElement>(null);

    const formRef = useRef<ManualEntryFormRefType>(undefined);

    const { 
        state,
        setSelectedDate,
        setEndDate,
        resetOptions,
        setFormState
    //} = useManualEntryFormState();
    } = manualEntryFormState;



    async function onSave() {

        const newValues = formRef.current?.getChanges() ?? [];

        try {
    
            if (newValues.length) {

                await TagValueDataService.saveTagValues(userState.customer?.id as number,
                    newValues, 
                    true,
                    offlineState.offline);

                acknowledge(offlineState.offline ? 
                    t('tag.offlineValuesStored') : 
                        t('tag.valuesSaved'));
            }


            setTimeout(() => {
                // need to wait for friggin' backend to propagate values
                // refetch does not always cause the data to be retreived again,
                // so force it by changing the end data paramater key
                //refetch();
                setSelectedDate(addSeconds(state.selectedDate, 1));
                setEndDate(addSeconds(state.endDate, 1));
            }, 1000);

        } catch(error) {

            const err = error as RestError;
                        
            setAppNotificationState({
                notificationType: AppNotificationTypeEnum.Error,
                message: err.message,
                show: true,
                error: err
            });
        }
    }


    async function getUserConfirmation() {

        const response = await confirm({
            dialogTitle: t('cancelConfirmation.title'),
            confirmationText: t('tag.cancelEditText'),
            yesButtonText: t('cancelConfirmation.yesButtonText'),
            noButtonText: t('cancelConfirmation.noButtonText'),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        });
        
        return response === "yes";
    };


    async function onDateChanged (date: Date | null) {

        if (date) {

            if (state.formState.isDirty) {
                const result = await getUserConfirmation();

                if (!result) {
                    return;
                }
            }

            // reset({
            //     ...defaultManualEntryForm
            // });

            setEndDate(date);
        }

        if (isPhone) {

            dispatchAppSettings({
                type: AppSettingsActionTypeEnum.SideSheetOpenChanged,
                open: false
            });
        }
    }
    
    function onFormStateChanged(data: ManualEntryFormStateType) {
        setFormState(data);
    }

    return (
        <ContentContainer>
            <ContentSection>

                <ManualEntryForm 
                    mode={ManualEntryFormMode.ManualEntry}
                    ref={formRef}
                    //date={state.endDate} 
                    date={state.selectedDate} 
                    formState={state.formState}
                    onFormStateChanged={onFormStateChanged}
                    elynxObject={elynxObject}
                />

                <UnsavedChangesPrompt
                    hasChanges={state.formState.isDirty}
                    getUserConfirmation={getUserConfirmation}
                />

                <Fab color="secondary"
                    id='save'
                    name='save'
                    type='button'
                    ref={buttonRef}
                    disabled={!state.formState.isDirty || !state.formState.isValid}
                    aria-label="save changes" 
                    sx={{
                        position: 'absolute',
                        bottom: theme.spacing(isPhone ? 2 : 5),
                        //right: theme.spacing(3)
                        right: !hideOptions && !isPhone && appSettings.sideSheetOpen ? '340px' : theme.spacing(4)
                    }}
                    //onClick={(e) => handleSubmit((data) => onSave(data))()}
                    onClick={() => onSave()}
                    >
                    <Save />
                </Fab>
            
            </ContentSection>
            {!hideOptions && <OptionsSideSheet onReset={() => resetOptions()}>

                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        flexGrow: 1,
                        padding: theme.spacing(1),
                        marginTop: isPhone ? theme.spacing(1) : undefined
                    }}>

                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DefaultDatePicker
                                label={t('manualEntry.pickerLabel')}
                                value={state.selectedDate}
                                maxDate={new Date()}
                                slotProps={{ textField: { variant: 'filled' } }}
                                onChange={(value) => { setSelectedDate(value as Date)}}
                                onAccept={(date: Date | null) => onDateChanged(date)}
                            />
                        </LocalizationProvider>
                    </Box>

                </OptionsSideSheet>
                }
        </ContentContainer>
    );
}


